import type { AuctisyncSupabase } from './client.js';
import {
  fetchMyInvoice,
  fetchMyInvoices,
  invoiceIsOutstanding,
  type MyInvoiceLine,
} from './invoices.js';
import { resolveTimeZone } from '@auctisync/shared';

import { fetchHouseContact, formatHouseAddress, type HouseContact } from './house-contact.js';

/**
 * Where and when a bidder collects what they won.
 *
 * 🔴 PICKUP DETAILS LIVE IN THREE PLACES, and that is the problem this module
 * exists to solve rather than a mess to tidy up later. The auction carries
 * typed address columns and free-text instructions; the invoice carries its own
 * `pickup_summary`; the house carries a general pickup window. All three are
 * legitimate — a sale can move premises for one auction, an invoice can carry a
 * note about one buyer's pallet — and a bidder must never be shown two of them
 * at once and left to guess.
 *
 * The design says why this matters more than it looks: "a second source for a
 * pickup time is a second thing to get wrong." So the precedence is decided
 * ONCE, here, and every surface asks this rather than picking a field.
 *
 * `auctions.pickup_dates` NOW HAS A CONTRACT and is read (2026-09-02). It had
 * none for a year — a jsonb column with no writer and no agreed shape — so this
 * module used to refuse it rather than invent one. The shape is written down on
 * the column itself (migration `20260902140000`) and the scheduler that will
 * populate it belongs to AUCTION CREATION, because a collection schedule is a
 * per-sale, per-tenant choice that depends on the style of auction.
 *
 * 🔴 UNTIL SOMETHING WRITES IT, EVERY WINDOW LIST IS EMPTY, and that is the
 * correct behaviour rather than a gap: prose like "Sat 9–4" is still shown,
 * and anything that needs an ABSOLUTE date — a calendar entry, a reminder, a
 * collect-by countdown — stays hidden. A calendar entry on the wrong Saturday
 * is worse than no button, because the bidder now believes they have a
 * reminder.
 */

/**
 * One collection window, with a real start.
 *
 * The whole point of this type is that it can answer "which Saturday?". A shape
 * that cannot is prose, and prose lives in `instructions`.
 */
export interface PickupWindow {
  /** ISO 8601. Required — a window without a start is not a window. */
  startsAt: string;
  endsAt: string | null;
  note: string | null;
}

/** Pickup as one auction states it. Every field optional — most sales fill in some. */
export interface AuctionPickup {
  auctionId: string;
  location: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postalCode: string | null;
  instructions: string | null;
  /** Scheduled collection windows, earliest first. */
  windows: PickupWindow[];
  /**
   * The sale's own IANA zone, when it has one.
   *
   * 🔴 COLLECTION TIMES ARE READ IN THE WAREHOUSE'S ZONE, NOT THE READER'S. A
   * buyer in another state still has to arrive when the gate is open, so "9am"
   * must mean 9am there. Carried on the resolved pickup so the phone and the
   * website cannot answer it differently.
   */
  timeZone: string | null;
}

const PICKUP_COLUMNS =
  'id, pickup_location, pickup_address_line1, pickup_address_line2, pickup_city, pickup_region, pickup_postal_code, pickup_instructions, pickup_dates, timezone';

function text(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * `auctions.pickup_dates` → windows, ignoring anything it cannot understand.
 *
 * TOLERANT ON PURPOSE. Nothing has ever written this column, so the first rows
 * it sees will come from a tool that does not exist yet, and possibly from a
 * hand-edit before that. An entry without a usable `starts_at` is DROPPED
 * rather than repaired or defaulted: a guessed date is the one outcome worse
 * than showing nothing, because it produces a confident wrong answer on a
 * screen somebody plans a drive around.
 *
 * Sorted earliest-first so `nextPickupWindow` can simply scan.
 */
export function parsePickupWindows(raw: unknown): PickupWindow[] {
  if (!Array.isArray(raw)) return [];

  const windows: PickupWindow[] = [];
  for (const entry of raw) {
    if (entry === null || typeof entry !== 'object') continue;
    const o = entry as Record<string, unknown>;

    const startsAt = text(o['starts_at']);
    // Not a date we can act on — and "we could not parse it" must never become
    // "today", which is what a fallback here would quietly mean.
    if (startsAt === null || Number.isNaN(Date.parse(startsAt))) continue;

    const rawEnd = text(o['ends_at']);
    const endsAt = rawEnd !== null && !Number.isNaN(Date.parse(rawEnd)) ? rawEnd : null;
    // An end before its start is corrupt, not a zero-length window. Dropping
    // just the end keeps the useful half rather than losing the whole entry.
    const usableEnd = endsAt !== null && Date.parse(endsAt) > Date.parse(startsAt) ? endsAt : null;

    windows.push({ startsAt, endsAt: usableEnd, note: text(o['note']) });
  }

  return windows.sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt));
}

/**
 * The window a bidder is actually driving to — the first that has not ENDED.
 *
 * Not the first that has not STARTED: a bidder standing in the yard at 10am on
 * a 9–4 day is inside the window, and telling them the next one is on Tuesday
 * would be both wrong and infuriating. A window with no end is treated as
 * finished once its start has passed, which is the cautious reading of an
 * open-ended entry.
 */
export function nextPickupWindow(
  windows: readonly PickupWindow[],
  nowMs: number = Date.now(),
): PickupWindow | null {
  return remainingPickupWindows(windows, nowMs)[0] ?? null;
}

/**
 * Every window a bidder can still make, earliest first.
 *
 * 🔴 ONE RULE FOR "STILL COUNTS", NOT TWO. `nextPickupWindow` is now the first
 * of these rather than its own loop, because the sale band lists all of them
 * (Jim, 2026-09-18, after adding a second collection time and finding only one
 * rendered) while the pickup screen names just the soonest. Two surfaces, one
 * question — and a second copy of "has this ended?" is how they would come to
 * disagree about a window that ends today.
 *
 * A window with no end is treated as finished once its start has passed, which
 * is the cautious reading of an open-ended entry.
 */
export function remainingPickupWindows(
  windows: readonly PickupWindow[],
  nowMs: number = Date.now(),
): PickupWindow[] {
  return windows.filter((w) => {
    const closesAt = w.endsAt !== null ? Date.parse(w.endsAt) : Date.parse(w.startsAt);
    return closesAt >= nowMs;
  });
}

/**
 * Pickup details for the auctions a bidder has invoices in.
 *
 * Batched by id rather than fetched per invoice: a bidder collecting from three
 * sales should cost one round trip, on a phone, standing in a yard.
 */
export async function fetchAuctionPickups(
  supabase: AuctisyncSupabase,
  auctionIds: readonly string[],
): Promise<Map<string, AuctionPickup>> {
  const ids = [...new Set(auctionIds)].filter((id) => id !== '');
  if (ids.length === 0) return new Map();

  const { data, error } = await supabase.from('auctions').select(PICKUP_COLUMNS).in('id', ids);
  if (error) return new Map();

  const out = new Map<string, AuctionPickup>();
  for (const row of data ?? []) {
    out.set(row.id, {
      auctionId: row.id,
      location: text(row.pickup_location),
      addressLine1: text(row.pickup_address_line1),
      addressLine2: text(row.pickup_address_line2),
      city: text(row.pickup_city),
      region: text(row.pickup_region),
      postalCode: text(row.pickup_postal_code),
      instructions: text(row.pickup_instructions),
      windows: parsePickupWindows(row.pickup_dates),
      timeZone: text(row.timezone),
    });
  }
  return out;
}

/** What a pickup surface should show, and where each line came from. */
export interface ResolvedPickup {
  /** When to come. Free text — see `resolvePickupWindow`. */
  window: string | null;
  /** Where to come. A one-line address a person could read out. */
  address: string | null;
  /** A named place, when the house gave one ("Bay 3, rear yard"). */
  location: string | null;
  /** Anything else the house said about collecting. */
  instructions: string | null;
  /**
   * The scheduled window a bidder is driving to, when one exists.
   *
   * 🔴 THIS IS THE GATE FOR EVERYTHING THAT NEEDS AN ABSOLUTE DATE — the
   * calendar entry, and later a pickup reminder or a collect-by countdown.
   * Null means the platform knows only prose, and those controls must stay
   * hidden rather than guess which Saturday.
   *
   * It is null everywhere today: the auction-creation scheduler that writes
   * `pickup_dates` does not exist yet. That is the plumbing working, not
   * missing.
   */
  nextWindow: PickupWindow | null;
  /**
   * The zone `nextWindow` should be READ in — always resolved, never null.
   *
   * Falls through the sale's own zone to the platform default, so a surface can
   * format without repeating the fallback chain and getting it subtly different.
   */
  timeZone: string;
  /**
   * Which source answered, so a surface can say "for this sale" rather than
   * implying a house-wide rule — and so a bug here is visible rather than
   * mysterious.
   */
  from: 'auction' | 'invoice' | 'house' | 'none';
}

export interface PickupSources {
  auction?: AuctionPickup | null;
  /** `invoices.pickup_summary` — free text, written per invoice. */
  invoiceSummary?: string | null;
  house?: HouseContact | null;
  /** Injected in tests; defaults to the wall clock. */
  nowMs?: number;
}

/**
 * The precedence, in one place.
 *
 * SPECIFIC BEATS GENERAL, always. An auction that states its own address has
 * said something about THIS sale, and it outranks the house's standing answer —
 * a sale held at a different yard is exactly the case where showing the usual
 * address sends someone to the wrong town. The invoice's free text sits between
 * them: it is about this bidder's collection specifically, but it is prose
 * rather than an address.
 *
 * Each field resolves independently. A sale that gave an address but no times
 * should show the sale's address and the house's hours, not fall back wholesale
 * to the house and quietly discard the address it was given.
 */
export function resolvePickup(sources: PickupSources): ResolvedPickup {
  const auction = sources.auction ?? null;
  const house = sources.house ?? null;

  const auctionAddress =
    auction === null
      ? null
      : formatHouseAddress({
          line1: auction.addressLine1,
          line2: auction.addressLine2,
          city: auction.city,
          region: auction.region,
          postalCode: auction.postalCode,
          country: null,
        });

  const houseAddress = house === null ? null : formatHouseAddress(house.address);
  const address = auctionAddress ?? houseAddress;

  const scheduled = nextPickupWindow(auction?.windows ?? [], sources.nowMs ?? Date.now());
  const instructions = auction?.instructions ?? null;

  // A SCHEDULED window outranks the prose, because it is the same fact stated
  // precisely — and the surface can then render it in the reader's own
  // timezone rather than repeating someone's typing.
  const window =
    scheduled !== null
      ? formatPickupWindow(scheduled)
      : (text(sources.invoiceSummary) ?? house?.pickupHours ?? null);

  const from: ResolvedPickup['from'] =
    scheduled !== null || auctionAddress !== null || instructions !== null
      ? 'auction'
      : text(sources.invoiceSummary) !== null
        ? 'invoice'
        : houseAddress !== null || house?.pickupHours != null
          ? 'house'
          : 'none';

  return {
    window,
    address,
    location: auction?.location ?? null,
    instructions,
    nextWindow: scheduled,
    timeZone: resolveTimeZone(auction?.timeZone),
    from,
  };
}

/**
 * A scheduled window, written the way a person says it.
 *
 * Rendered in the READER's timezone, deliberately. The stored value is absolute
 * UTC, so a bidder who has driven two states over is told the local time at
 * which the yard is actually open — which is the entire advantage a real
 * timestamp has over "Sat 9–4".
 */
export function formatPickupWindow(w: PickupWindow, locale?: string): string {
  const start = new Date(w.startsAt);
  const day = start.toLocaleDateString(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const time = (d: Date) => d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });

  if (w.endsAt === null) return `${day}, from ${time(start)}`;
  return `${day}, ${time(start)}–${time(new Date(w.endsAt))}`;
}

/**
 * Everything a calendar entry needs, or null when there is no date to put in one.
 *
 * NULL IS THE COMMON CASE TODAY and is the whole safety property: a surface
 * asks for an event, gets nothing, and shows no button. It cannot accidentally
 * create an entry on a guessed day.
 *
 * The notes carry what the counter asks for — the paddle number and the invoice
 * — because a calendar entry is read at the moment of leaving the house, which
 * is exactly when a bidder wants to know what to take.
 */
export interface PickupCalendarEvent {
  title: string;
  startsAt: string;
  /** Absent in the source becomes a sensible span; see the note in the body. */
  endsAt: string;
  location: string | null;
  notes: string | null;
}

export interface PickupCalendarInput {
  pickup: ResolvedPickup;
  houseName: string;
  paddleNumber?: number | null;
  invoiceNumber?: string | null;
}

/** How long an open-ended window is assumed to run, in ms. */
const OPEN_ENDED_WINDOW_MS = 60 * 60 * 1000;

export function calendarEventForPickup(input: PickupCalendarInput): PickupCalendarEvent | null {
  const w = input.pickup.nextWindow;
  if (w === null) return null;

  // A calendar needs an end. An hour is a guess, but it is a guess about
  // DURATION on a day we know — categorically different from guessing the day,
  // and every calendar app lets the owner drag it.
  const endsAt = w.endsAt ?? new Date(Date.parse(w.startsAt) + OPEN_ENDED_WINDOW_MS).toISOString();

  const notes = [
    input.paddleNumber != null ? `Paddle ${String(input.paddleNumber)}` : null,
    input.invoiceNumber != null ? `Invoice ${input.invoiceNumber}` : null,
    'Bring photo ID if someone else is collecting for you.',
    w.note,
    input.pickup.instructions,
  ]
    .filter((line): line is string => line !== null && line !== '')
    .join('\n');

  return {
    title: `Collect from ${input.houseName}`,
    startsAt: w.startsAt,
    endsAt,
    location: input.pickup.address,
    notes: notes === '' ? null : notes,
  };
}

/**
 * A maps link for an address, or null.
 *
 * `geo:` on Android and Apple Maps on iOS both accept a plain query, so the
 * address is passed as text rather than parsed into coordinates — we do not
 * have coordinates, and geocoding a yard address ourselves would be a worse
 * answer than letting the maps app do it.
 */
export function directionsUrl(address: string | null, platform: 'ios' | 'android'): string | null {
  if (address === null || address.trim() === '') return null;
  const q = encodeURIComponent(address.trim());
  return platform === 'ios' ? `http://maps.apple.com/?q=${q}` : `geo:0,0?q=${q}`;
}

/**
 * One sale a bidder still has to collect from.
 *
 * The LINES are what make a pickup surface worth opening — "three lots, and
 * here they are" rather than a total.
 */
export interface PickupSale {
  invoiceId: string;
  invoiceNumber: string | null;
  auctionId: string;
  auctionTitle: string;
  outstanding: boolean;
  pickup: ResolvedPickup;
  lines: MyInvoiceLine[];
  notes: string | null;
}

/**
 * Everything a bidder still has to collect, assembled.
 *
 * EXTRACTED FROM THE PHONE'S PICKUP SCREEN (2026-09-03) when the website grew
 * the same section. Four reads have to happen in a particular order and be
 * combined by a particular precedence, and a second copy of that is a second
 * place for "which Saturday?" to come out differently on two surfaces a bidder
 * uses interchangeably.
 *
 * ONLY WHAT IS STILL TO BE COLLECTED. A paid-and-collected invoice from March
 * is history; this answers "what do I still have to do", and putting the old
 * ones here would bury the sale somebody is driving to today.
 */
export async function fetchPickupSales(
  supabase: AuctisyncSupabase,
  params: { userId: string; tenantId: string | null; nowMs?: number },
): Promise<PickupSale[]> {
  const invoices = await fetchMyInvoices(supabase, params.userId);
  const relevant = invoices.filter((i) => invoiceIsOutstanding(i.status));
  if (relevant.length === 0) return [];

  const [pickups, house] = await Promise.all([
    fetchAuctionPickups(
      supabase,
      relevant.map((i) => i.auction_id),
    ),
    params.tenantId === null ? Promise.resolve(null) : fetchHouseContact(supabase, params.tenantId),
  ]);

  const built: PickupSale[] = [];
  for (const invoice of relevant) {
    // One read per outstanding invoice, and a bidder has a handful at most.
    const detail = await fetchMyInvoice(supabase, invoice.id);
    built.push({
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoice_number,
      auctionId: invoice.auction_id,
      auctionTitle: invoice.auction?.title ?? 'Auction',
      outstanding: true,
      pickup: resolvePickup({
        auction: pickups.get(invoice.auction_id) ?? null,
        invoiceSummary: invoice.pickup_summary,
        house,
        ...(params.nowMs === undefined ? {} : { nowMs: params.nowMs }),
      }),
      lines: detail?.lines ?? [],
      notes: invoice.notes,
    });
  }
  return built;
}
